import { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { 
  MessageSquare, 
  Users, 
  AlertTriangle, 
  Phone, 
  Mail, 
  User,
  Send,
  RefreshCw,
  Volume2,
  VolumeX,
  ExternalLink,
  Clock,
  Bot,
  UserCheck,
  Save,
  FileText
} from 'lucide-react';

const API_URL = window.location.origin;

export function ContactCenterDashboard() {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [stats, setStats] = useState({});
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [agentInfo, setAgentInfo] = useState(null);
  const [zohoLead, setZohoLead] = useState(null);
  const messagesEndRef = useRef(null);
  const audioRef = useRef(null);
  const wsRef = useRef(null);

  // Get agent info from localStorage
  useEffect(() => {
    const adminData = localStorage.getItem('cms_admin');
    if (adminData) {
      const admin = JSON.parse(adminData);
      setAgentInfo({
        id: admin.id || admin.email,
        name: admin.name || admin.email.split('@')[0]
      });
    }
  }, []);

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/chat/sessions/active`);
      const data = await response.json();
      setSessions(data.sessions || []);
      setStats({
        total: data.total,
        escalated: data.escalated_count,
        humanMode: data.human_mode_count,
        isWorkHours: data.is_work_hours
      });
    } catch (error) {
      console.error('Error fetching sessions:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch and polling
  useEffect(() => {
    fetchSessions();
    const interval = setInterval(fetchSessions, 5000); // Poll every 5 seconds
    return () => clearInterval(interval);
  }, [fetchSessions]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!agentInfo) return;

    const connectWebSocket = () => {
      const wsUrl = API_URL.replace('https://', 'wss://').replace('http://', 'ws://');
      const ws = new WebSocket(`${wsUrl}/api/chat/ws/agent/${agentInfo.id}`);
      
      ws.onopen = () => {
        console.log('Agent WebSocket connected');
      };

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        
        if (data.type === 'new_message') {
          // Update session in list
          setSessions(prev => prev.map(s => 
            s.id === data.session_id 
              ? { ...s, messages: [...(s.messages || []), data.message], updated_at: new Date().toISOString() }
              : s
          ));
          
          // If this is the selected session, update it
          if (selectedSession?.id === data.session_id) {
            setSelectedSession(prev => ({
              ...prev,
              messages: [...(prev.messages || []), data.message]
            }));
          }
          
          // Play notification sound
          if (soundEnabled && audioRef.current) {
            audioRef.current.play().catch(() => {});
          }
        }
        
        if (data.type === 'escalation' && soundEnabled) {
          // Play alert sound for escalation
          if (audioRef.current) {
            audioRef.current.play().catch(() => {});
          }
          fetchSessions(); // Refresh to show escalation
        }
        
        if (data.type === 'init') {
          setSessions(data.sessions || []);
        }
      };

      ws.onclose = () => {
        console.log('Agent WebSocket disconnected, reconnecting...');
        setTimeout(connectWebSocket, 3000);
      };

      wsRef.current = ws;
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [agentInfo, soundEnabled, selectedSession?.id, fetchSessions]);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedSession?.messages]);

  // Fetch Zoho lead info when session selected
  useEffect(() => {
    if (selectedSession?.id) {
      fetchZohoLead(selectedSession.id);
    }
  }, [selectedSession?.id]);

  const fetchZohoLead = async (sessionId) => {
    try {
      const response = await fetch(`${API_URL}/api/chat/session/${sessionId}/zoho-lead`);
      const data = await response.json();
      setZohoLead(data);
    } catch (error) {
      console.error('Error fetching Zoho lead:', error);
      setZohoLead(null);
    }
  };

  const handleSelectSession = async (session) => {
    // Fetch full session details
    try {
      const response = await fetch(`${API_URL}/api/chat/session/${session.id}`);
      const fullSession = await response.json();
      setSelectedSession(fullSession);
    } catch (error) {
      console.error('Error fetching session:', error);
      setSelectedSession(session);
    }
  };

  const handleTakeover = async () => {
    if (!selectedSession || !agentInfo) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/chat/session/${selectedSession.id}/takeover?agent_id=${agentInfo.id}&agent_name=${encodeURIComponent(agentInfo.name)}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        // Update local state
        setSelectedSession(prev => ({
          ...prev,
          mode: 'human',
          assigned_agent_id: agentInfo.id,
          assigned_agent_name: agentInfo.name
        }));
        fetchSessions();
      }
    } catch (error) {
      console.error('Error taking over session:', error);
    }
  };

  const handleRelease = async () => {
    if (!selectedSession || !agentInfo) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/chat/session/${selectedSession.id}/release?agent_id=${agentInfo.id}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        setSelectedSession(prev => ({
          ...prev,
          mode: 'ai',
          assigned_agent_id: null,
          assigned_agent_name: null
        }));
        fetchSessions();
      }
    } catch (error) {
      console.error('Error releasing session:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || !selectedSession || !agentInfo) return;
    
    setSending(true);
    try {
      const response = await fetch(
        `${API_URL}/api/chat/session/${selectedSession.id}/agent-message?content=${encodeURIComponent(message)}&agent_id=${agentInfo.id}&agent_name=${encodeURIComponent(agentInfo.name)}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setSelectedSession(prev => ({
          ...prev,
          messages: [...(prev.messages || []), data.message],
          mode: 'human'
        }));
        setMessage('');
        fetchSessions();
      }
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const [savingToZoho, setSavingToZoho] = useState(false);

  const handleSaveToZoho = async () => {
    if (!selectedSession || !agentInfo || !zohoLead?.zoho_lead_id) return;
    
    setSavingToZoho(true);
    try {
      const response = await fetch(
        `${API_URL}/api/chat/session/${selectedSession.id}/save-to-zoho?agent_id=${agentInfo.id}&agent_name=${encodeURIComponent(agentInfo.name)}`,
        { method: 'POST' }
      );
      
      if (response.ok) {
        const data = await response.json();
        alert(`Chat saved to Zoho Notes!\nNote ID: ${data.note_id}`);
        // Update local state
        setSelectedSession(prev => ({
          ...prev,
          zoho_note_id: data.note_id,
          zoho_note_saved_at: new Date().toISOString()
        }));
      } else {
        const error = await response.json();
        alert(`Failed to save: ${error.detail || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error saving to Zoho:', error);
      alert('Failed to save chat to Zoho');
    } finally {
      setSavingToZoho(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    }
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const getEscalationBadge = (reason) => {
    const badges = {
      'user_requested': { label: 'User Requested', color: 'bg-red-500' },
      'high_intent': { label: 'High Intent', color: 'bg-orange-500' },
      'message_threshold': { label: '5+ Messages', color: 'bg-yellow-500' }
    };
    return badges[reason] || { label: reason, color: 'bg-gray-500' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col">
      {/* Hidden audio element for notifications */}
      <audio ref={audioRef} src="https://assets.mixkit.co/active_storage/sfx/2869/2869-preview.mp3" preload="auto" />
      
      {/* Back to Dashboard Button */}
      <div className="mb-4">
        <Button
          variant="outline"
          onClick={() => window.location.href = '/admin'}
          className="flex items-center gap-2"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Dashboard
        </Button>
      </div>
      
      {/* Header Stats */}
      <div className="grid grid-cols-5 gap-4 mb-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-2xl font-bold">{stats.total || 0}</p>
              <p className="text-sm text-gray-500">Active Chats</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className={stats.escalated > 0 ? 'border-orange-500 border-2' : ''}>
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className={`w-8 h-8 ${stats.escalated > 0 ? 'text-orange-500 animate-pulse' : 'text-gray-400'}`} />
            <div>
              <p className="text-2xl font-bold">{stats.escalated || 0}</p>
              <p className="text-sm text-gray-500">Escalated</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <UserCheck className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-2xl font-bold">{stats.humanMode || 0}</p>
              <p className="text-sm text-gray-500">Human Mode</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Clock className={`w-8 h-8 ${stats.isWorkHours ? 'text-green-500' : 'text-gray-400'}`} />
            <div>
              <p className="text-sm font-medium">{stats.isWorkHours ? 'Work Hours' : 'After Hours'}</p>
              <p className="text-xs text-gray-500">Mon-Sat, 10AM-6PM</p>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="w-full justify-start"
            >
              {soundEnabled ? (
                <><Volume2 className="w-5 h-5 mr-2 text-green-500" /> Sound On</>
              ) : (
                <><VolumeX className="w-5 h-5 mr-2 text-gray-400" /> Sound Off</>
              )}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <div className="flex-1 grid grid-cols-12 gap-4 min-h-0">
        {/* Session List */}
        <Card className="col-span-4 flex flex-col">
          <CardHeader className="py-3 px-4 border-b">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg">Chat Sessions</CardTitle>
              <Button variant="ghost" size="sm" onClick={fetchSessions}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-2">
              {sessions.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No active sessions</p>
              ) : (
                sessions.map((session) => (
                  <div
                    key={session.id}
                    onClick={() => handleSelectSession(session)}
                    className={`p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedSession?.id === session.id 
                        ? 'bg-blue-50 border border-blue-200' 
                        : 'hover:bg-gray-50 border border-transparent'
                    } ${session.is_escalated ? 'border-l-4 border-l-orange-500' : ''}`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-sm">
                          {session.visitor_name || 'Anonymous'}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400">
                        {formatTime(session.updated_at)}
                      </span>
                    </div>
                    
                    <p className="text-xs text-gray-500 truncate mb-2">
                      {session.last_message?.content || 'No messages'}
                    </p>
                    
                    <div className="flex items-center gap-2 flex-wrap">
                      {session.mode === 'human' && (
                        <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                          <UserCheck className="w-3 h-3 mr-1" />
                          {session.assigned_agent_name || 'Human'}
                        </Badge>
                      )}
                      {session.mode === 'ai' && (
                        <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                          <Bot className="w-3 h-3 mr-1" />
                          AI
                        </Badge>
                      )}
                      {session.escalation_reason && (
                        <Badge className={`text-xs ${getEscalationBadge(session.escalation_reason).color}`}>
                          {getEscalationBadge(session.escalation_reason).label}
                        </Badge>
                      )}
                      {session.visitor_email && (
                        <Badge variant="outline" className="text-xs">
                          <Mail className="w-3 h-3 mr-1" />
                          Email
                        </Badge>
                      )}
                      {session.visitor_phone && (
                        <Badge variant="outline" className="text-xs">
                          <Phone className="w-3 h-3 mr-1" />
                          Phone
                        </Badge>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </Card>

        {/* Chat Panel */}
        <Card className="col-span-5 flex flex-col">
          {selectedSession ? (
            <>
              <CardHeader className="py-3 px-4 border-b">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">
                      {selectedSession.visitor_name || 'Anonymous Visitor'}
                    </CardTitle>
                    <p className="text-xs text-gray-500">
                      Session: {selectedSession.id} | Messages: {selectedSession.messages?.length || 0}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    {selectedSession.mode === 'ai' ? (
                      <Button size="sm" onClick={handleTakeover} className="bg-green-600 hover:bg-green-700">
                        <UserCheck className="w-4 h-4 mr-1" />
                        Take Over
                      </Button>
                    ) : selectedSession.assigned_agent_id === agentInfo?.id ? (
                      <Button size="sm" variant="outline" onClick={handleRelease}>
                        Release to AI
                      </Button>
                    ) : (
                      <Badge variant="secondary" className="bg-green-100">
                        Handled by {selectedSession.assigned_agent_name}
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-3">
                  {selectedSession.messages?.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`flex ${msg.sender === 'visitor' ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg ${
                          msg.sender === 'visitor'
                            ? 'bg-gray-100 text-gray-800'
                            : msg.sender === 'ai'
                            ? 'bg-blue-100 text-blue-800'
                            : msg.sender === 'system'
                            ? 'bg-yellow-50 text-yellow-800 text-center w-full'
                            : 'bg-green-100 text-green-800'
                        }`}
                      >
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs opacity-60">
                            {msg.sender === 'ai' ? 'AI' : msg.sender === 'agent' ? msg.agent_name : msg.sender}
                          </span>
                          <span className="text-xs opacity-60">{formatTime(msg.timestamp)}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>
              
              {/* Message Input */}
              <div className="p-4 border-t">
                <form onSubmit={handleSendMessage} className="flex gap-2">
                  <Input
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder={
                      selectedSession.mode === 'human' && selectedSession.assigned_agent_id === agentInfo?.id
                        ? "Type your message..."
                        : "Take over to send messages..."
                    }
                    disabled={selectedSession.mode === 'ai' || (selectedSession.mode === 'human' && selectedSession.assigned_agent_id !== agentInfo?.id)}
                    className="flex-1"
                  />
                  <Button 
                    type="submit" 
                    disabled={sending || !message.trim() || selectedSession.mode === 'ai'}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
                {selectedSession.mode === 'ai' && (
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Click "Take Over" to start responding to this chat
                  </p>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Select a chat session to view</p>
              </div>
            </div>
          )}
        </Card>

        {/* Visitor Details Panel */}
        <Card className="col-span-3 flex flex-col">
          <CardHeader className="py-3 px-4 border-b">
            <CardTitle className="text-lg">Visitor Details</CardTitle>
          </CardHeader>
          <ScrollArea className="flex-1">
            {selectedSession ? (
              <div className="p-4 space-y-4">
                {/* Contact Info */}
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-gray-500 uppercase">Contact</h4>
                  
                  {selectedSession.visitor_name && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="text-sm">{selectedSession.visitor_name}</span>
                    </div>
                  )}
                  
                  {selectedSession.visitor_email && (
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <a href={`mailto:${selectedSession.visitor_email}`} className="text-sm text-blue-600 hover:underline">
                        {selectedSession.visitor_email}
                      </a>
                    </div>
                  )}
                  
                  {selectedSession.visitor_phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-gray-400" />
                      <a href={`tel:${selectedSession.visitor_phone}`} className="text-sm text-blue-600 hover:underline">
                        {selectedSession.visitor_phone}
                      </a>
                    </div>
                  )}
                  
                  {!selectedSession.visitor_name && !selectedSession.visitor_email && !selectedSession.visitor_phone && (
                    <p className="text-sm text-gray-400">No contact info provided</p>
                  )}
                </div>

                {/* Session Info */}
                <div className="space-y-3 pt-4 border-t">
                  <h4 className="font-medium text-sm text-gray-500 uppercase">Session</h4>
                  
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-gray-500">Started:</span>
                    <span>{formatDate(selectedSession.created_at)} {formatTime(selectedSession.created_at)}</span>
                    
                    <span className="text-gray-500">Messages:</span>
                    <span>{selectedSession.messages?.length || 0}</span>
                    
                    <span className="text-gray-500">Mode:</span>
                    <span className={selectedSession.mode === 'human' ? 'text-green-600' : 'text-blue-600'}>
                      {selectedSession.mode === 'human' ? 'Human' : 'AI'}
                    </span>
                    
                    {selectedSession.destination_interest && (
                      <>
                        <span className="text-gray-500">Destination:</span>
                        <span>{selectedSession.destination_interest}</span>
                      </>
                    )}
                  </div>
                  
                  {selectedSession.escalation_reason && (
                    <div className="mt-2">
                      <Badge className={getEscalationBadge(selectedSession.escalation_reason).color}>
                        Escalated: {getEscalationBadge(selectedSession.escalation_reason).label}
                      </Badge>
                    </div>
                  )}
                </div>

                {/* Zoho Lead Info */}
                <div className="space-y-3 pt-4 border-t">
                  <h4 className="font-medium text-sm text-gray-500 uppercase">Zoho CRM</h4>
                  
                  {zohoLead?.has_lead ? (
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-gray-500">Lead ID:</span>
                        <span className="font-mono text-xs">{zohoLead.zoho_lead_id || 'N/A'}</span>
                        
                        <span className="text-gray-500">Status:</span>
                        <Badge variant={zohoLead.zoho_sync_status === 'synced' ? 'default' : 'secondary'}>
                          {zohoLead.zoho_sync_status || 'Unknown'}
                        </Badge>
                      </div>
                      
                      {zohoLead.zoho_lead_id && (
                        <div className="space-y-2 mt-3">
                          <Button variant="outline" size="sm" className="w-full" asChild>
                            <a 
                              href={`https://crm.zoho.in/crm/org60023704312/tab/Leads/${zohoLead.zoho_lead_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="w-4 h-4 mr-2" />
                              View in Zoho
                            </a>
                          </Button>
                          
                          {/* Save Chat to Zoho Notes */}
                          {selectedSession.zoho_note_id ? (
                            <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 p-2 rounded">
                              <FileText className="w-4 h-4" />
                              <span>Chat saved to Zoho Notes</span>
                            </div>
                          ) : (
                            <Button 
                              variant="default" 
                              size="sm" 
                              className="w-full bg-blue-600 hover:bg-blue-700"
                              onClick={handleSaveToZoho}
                              disabled={savingToZoho}
                            >
                              {savingToZoho ? (
                                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              ) : (
                                <Save className="w-4 h-4 mr-2" />
                              )}
                              Save Chat to Zoho Notes
                            </Button>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400">No lead record found</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500 p-8">
                <p className="text-center text-sm">Select a session to view details</p>
              </div>
            )}
          </ScrollArea>
        </Card>
      </div>
    </div>
  );
}

export default ContactCenterDashboard;
